<template>
  <div v-if="csr" class="csr-strip-wrap">
    <!-- Strip -->
    <div
      class="csr-strip d-flex align-items-center border text-dark"
      role="button"
      tabindex="0"
      @click="open"
      @keydown.enter.prevent="open"
    >
      <div class="csr-icon d-flex align-items-center justify-content-center bg-primary text-light">
        <b-icon-heart-fill />
      </div>

      <div class="flex-grow-1 min-w-0 ml-2 d-flex align-items-center">
        <div class="csr-amount text-dark font-weight-bold">
          {{ donationAmount }}
          <span class="csr-currency font-weight-semibold">{{ currencyLabel }}</span>
        </div>

        <div class="csr-sep mx-2" />

        <div class="csr-label text-terciary text-truncate">
        <span>{{ $t("dashboard.csr_raised_for_prefix") }}</span>
        <strong class="csr-charity">{{ charityName }}</strong>
        </div>

      </div>

      <b-button
        size="sm"
        variant="outline-primary"
        class="csr-btn ml-2"
        @click.stop="open"
      >
        {{ $t("dashboard.csr_details") }}
      </b-button>
    </div>

    <!-- Modal -->
    <b-modal ref="modal" hide-footer centered size="md">
      <div class="d-flex align-items-start" style="gap: 12px;">
        <div class="csr-modal-icon d-flex align-items-center justify-content-center bg-primary text-light">
          <b-icon-heart-fill />
        </div>

        <div class="flex-grow-1 min-w-0">
          <div class="h3 mb-1 text-dark font-weight-bold">{{ charityName }}</div>
        </div>

        <b-button variant="link" class="p-0 text-terciary" @click="$refs.modal.hide()" aria-label="Close">
          <b-icon icon="x" />
        </b-button>
      </div>

      <div class="csr-big mt-3 text-dark font-weight-bold">
        {{ donationAmount }}
        <span class=" font-weight-semibold">{{ currencyLabel }}</span>
      </div>

      <div class="text-terciary small mt-0">
        <span v-if="capReached">{{ $t("dashboard.csr_cap_reached") }}</span>
        <span v-else>
          {{ $t("dashboard.csr_raised_so_far_max", { max: maxDonationAmount, currency: currencyLabel }) }}
        </span>
      </div>

      <div class="csr-points mt-3 border border-success bg-light p-3">
        <div class="d-flex" style="gap: 12px;">
          <div class="csr-points-icon d-flex align-items-center justify-content-center text-success">
            <b-icon-star-fill />
          </div>
          <div>
            <div class="font-weight-bold text-dark">
              {{ totalPoints }} {{ $t("dashboard.csr_points") }}
            </div>
            <div class="text-terciary small">
              {{ $t("dashboard.csr_points_earned_org") }}
            </div>
          </div>
        </div>
      </div>

      <div class="csr-rule mt-3 border text-terciary bg-light p-3">
       <div class="d-flex" style="gap: 10px;">
          <b-icon-info-circle class="csr-rule-icon" />
          <div class="text-terciary">
            {{ $t("dashboard.csr_rule_per_points", { rate: displayRate, currency: currencyLabel, per_points: displayPerPoints, max: maxDonationAmount }) }}
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BIconHeartFill, BIconStarFill, BIconInfoCircle } from "bootstrap-vue"

export default {
  name: "CsrStrip",
  components: {
    BIconHeartFill,
    BIconStarFill,
    BIconInfoCircle,
  },  
  props: {
    csr: { type: Object, default: null }
  },
  computed: {
    displayPerPoints() {
    const d = this.csr && this.csr.rule && this.csr.rule.display
    const v = d && Number(d.per_points)
    return Number.isFinite(v) && v > 0 ? v : 100
    },

    displayRate() {
    const d = this.csr && this.csr.rule && this.csr.rule.display
    const v = d && Number(d.rate)
    if (Number.isFinite(v) && v >= 0) return Math.round(v)

    const rpp = Number(this.ratePerPoint)
    const per = Number(this.displayPerPoints)
    const calc = rpp * per
    return Math.round(Number.isFinite(calc) ? calc : 0)
    },

    logoUrl() {
    const c = this.csr && this.csr.charity
    const p = c && typeof c.logo_path === "string" ? c.logo_path.trim() : ""
    return p || ""
    },    
    charityName() {
      return (this.csr && this.csr.charity && this.csr.charity.name) || ""
    },
    capReached() {
      return !!(this.csr && this.csr.totals && this.csr.totals.cap_reached)
    },
    ratePerPoint() {
      const v = this.csr && this.csr.rule && this.csr.rule.rate_per_point
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    },
    currencyLabel() {
      const code = (this.csr && this.csr.currency) || ""
      const map = { SEK: "kr", NOK: "kr", DKK: "kr", EUR: "€", USD: "$", GBP: "£", AUD: "A$", NZD: "NZ$" }
      return map[code] || code
    },
    donationAmount() {
    return this.formatMoneyInt(this.csr && this.csr.totals && this.csr.totals.donation_amount)
    },
    maxDonationAmount() {
    return this.formatMoneyInt(this.csr && this.csr.totals && this.csr.totals.max_donation_amount)
    },
    totalPoints() {
      return this.formatInt(this.csr && this.csr.totals && this.csr.totals.total_points)
    }
  },
  methods: {
    formatMoneyInt(n) {
    const v = Number(n)
    if (!Number.isFinite(v)) return "0"
    return new Intl.NumberFormat().format(Math.round(v))
    },
    open() {
      if (this.$refs.modal) this.$refs.modal.show()
    },
    formatInt(n) {
      const v = Math.max(0, Math.round(Number(n) || 0))
      try { return new Intl.NumberFormat().format(v) } catch (e) { return String(v) }
    }
  }
}
</script>

<style scoped>
/* No hardcoded hex colors — only layout/shape */
.csr-strip{
  border-radius: 15px 15px 0 0;
  padding: 14px 16px;

  /* make it feel like a card */
  box-shadow: 0 10px 22px rgba(0,0,0,0.08);
}

.csr-icon{
  width: 44px;
  height: 44px;
  border-radius: 999px;
  flex: 0 0 auto;
}

.csr-amount{
  font-size: 18px;
  font-weight: 800;
  white-space: nowrap;
  line-height: 1.1;
}

.csr-currency{
  font-size: 13px;
  font-weight: 700;
}

.csr-label{
  font-size: 14px;
  font-weight: 600;
}

.csr-sep{
  width: 1px;
  height: 20px;
  opacity: 0.25;
}

.csr-btn{
  font-weight: 700;
  padding-left: 14px;
  padding-right: 14px;
  border-radius: 10px;
}

.csr-modal-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  flex: 0 0 auto;
}

.csr-big {
  font-size: 38px;
}


.csr-points,
.csr-rule {
  border-radius: 14px;
}

.csr-points-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  flex: 0 0 auto;
}

.csr-charity{
  font-weight: 700;
}

.csr-strip{
  /* ...keep your existing padding/radius/shadow... */

  background: linear-gradient(
    135deg,
    #fef0f5 0%,
    #fff0f5 50%,
    #fdf9e6 100%
  );

  /* optional: slightly warmer border to match */
  border-color: #f5d5e2 !important;
}

.csr-strip:hover{
  background: linear-gradient(
    135deg,
    #fce8f0 0%,
    #fde8ef 50%,
    #fdf6dc 100%
  );
}

.csr-rule-icon{
  flex: 0 0 auto;
  margin-top: 4px; /* aligns with first text line */
}

</style>
